// Draw a signature once with a mouse, finger or pen; it goes on invoices
// above "Authorised signature" as a transparent PNG.
import { useEffect, useRef, useState } from 'react';
import { t } from '@/l10n/strings';
import { Button } from '@/ui/controls';
import { Dialog } from '@/ui/Dialog';

const WIDTH = 600;
const HEIGHT = 200;

export function SignaturePad({ onClose, onSave }: { onClose: () => void; onSave: (pngDataUri: string) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [empty, setEmpty] = useState(true);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const c = canvas.current!;
    const ctx = c.getContext('2d')!;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#12171A';
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * WIDTH, y: ((e.clientY - rect.top) / rect.height) * HEIGHT };
  };

  const clear = () => {
    canvas.current!.getContext('2d')!.clearRect(0, 0, WIDTH, HEIGHT);
    setEmpty(true);
  };

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t('customizeYourSignature')}
      actions={
        <>
          <Button variant="ghost" onClick={clear} disabled={empty}>
            {t('customizeClear')}
          </Button>
          <Button variant="action" disabled={empty} onClick={() => onSave(canvas.current!.toDataURL('image/png'))}>
            {t('customizeUseThisSignature')}
          </Button>
        </>
      }
    >
      <p className="caption">{t('webSignatureHelp')}</p>
      <canvas
        ref={canvas}
        width={WIDTH}
        height={HEIGHT}
        className="signature-pad"
        role="img"
        aria-label={t('webSignaturePad')}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          last.current = point(e);
        }}
        onPointerMove={(e) => {
          if (!last.current) return;
          const ctx = e.currentTarget.getContext('2d')!;
          const p = point(e);
          ctx.beginPath();
          ctx.moveTo(last.current.x, last.current.y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          last.current = p;
          setEmpty(false);
        }}
        onPointerUp={() => (last.current = null)}
        onPointerCancel={() => (last.current = null)}
      />
    </Dialog>
  );
}
